/**
 * Shared UI primitives.
 *
 * Every spacing and alignment utility here is a *logical* property
 * (`ms-`/`me-`, `ps-`/`pe-`, `start-`/`end-`, `text-start`) rather than a
 * physical one (`ml-`, `pr-`, `left-`, `text-left`). That is what makes the
 * entire interface mirror correctly when `dir="rtl"` is set, with no separate
 * Arabic stylesheet and no per-component conditionals.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { forwardRef, useEffect, useId, useRef } from 'react'

import { useTranslation } from 'react-i18next'

/**
 * Join class names, dropping anything falsy.
 *
 * Accepts numbers so `count && 'cls'` style expressions type-check; a falsy
 * number is filtered out rather than rendered as "0".
 */
export const cx = (...classes: (string | number | false | null | undefined)[]): string =>
  classes.filter((value): value is string | number => Boolean(value)).join(' ')

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
type ButtonSize = 'sm' | 'md' | 'lg'

const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-brand-contrast hover:bg-brand-strong shadow-sm',
  secondary:
    'bg-surface text-ink border border-border-subtle hover:bg-surface-sunken shadow-sm',
  ghost: 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
  danger: 'bg-danger text-white hover:brightness-95 shadow-sm',
  success: 'bg-success text-white hover:brightness-95 shadow-sm',
}

const buttonSizes: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-4 py-2.5 text-sm gap-2',
  lg: 'px-5 py-3 text-base gap-2',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  block?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, block = false, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center rounded-lg font-medium transition',
        'disabled:cursor-not-allowed disabled:opacity-55',
        buttonVariants[variant],
        buttonSizes[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  )
})

// ---------------------------------------------------------------------------
// Spinner
// ---------------------------------------------------------------------------
export const Spinner = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) => {
  const dimensions = { sm: 'size-4', md: 'size-6', lg: 'size-9' }[size]
  return (
    <span
      className={cx(
        dimensions,
        'inline-block shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent',
      )}
      aria-hidden="true"
    />
  )
}

export const LoadingBlock = ({ label }: { label?: string }) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-ink-subtle" role="status">
      <Spinner size="lg" />
      <span className="text-sm">{label ?? t('common.loading')}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------
export const Card = ({
  children,
  className,
  as: Tag = 'div',
}: {
  children: ReactNode
  className?: string
  as?: 'div' | 'article' | 'section' | 'li'
}) => (
  <Tag
    className={cx(
      'rounded-[var(--radius-card)] border border-border-subtle bg-surface shadow-[var(--shadow-card)]',
      className,
    )}
  >
    {children}
  </Tag>
)

export const SectionHeading = ({
  title,
  action,
  description,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
}) => (
  <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
    <div>
      <h2 className="text-lg font-semibold text-ink sm:text-xl">{title}</h2>
      {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
    </div>
    {action}
  </div>
)

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------
type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'accent'

const badgeTones: Record<BadgeTone, string> = {
  neutral: 'bg-surface-sunken text-ink-muted border-border-subtle',
  brand: 'bg-brand-soft text-brand-strong border-transparent',
  success: 'bg-success-soft text-success border-transparent',
  warning: 'bg-warning-soft text-warning border-transparent',
  danger: 'bg-danger-soft text-danger border-transparent',
  info: 'bg-info-soft text-info border-transparent',
  accent: 'bg-accent-soft text-accent border-transparent',
}

export const Badge = ({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode
  tone?: BadgeTone
  className?: string
}) => (
  <span
    className={cx(
      'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
      badgeTones[tone],
      className,
    )}
  >
    {children}
  </span>
)

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------
type AlertTone = 'info' | 'success' | 'warning' | 'danger'

const alertTones: Record<AlertTone, string> = {
  info: 'bg-info-soft text-ink border-info/25',
  success: 'bg-success-soft text-ink border-success/25',
  warning: 'bg-warning-soft text-ink border-warning/30',
  danger: 'bg-danger-soft text-ink border-danger/25',
}

export const Alert = ({
  tone = 'info',
  title,
  children,
  onDismiss,
  className,
}: {
  tone?: AlertTone
  title?: ReactNode
  children?: ReactNode
  onDismiss?: () => void
  className?: string
}) => {
  const { t } = useTranslation()
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cx('rounded-xl border px-4 py-3 text-sm', alertTones[tone], className)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {title && <p className="font-semibold">{title}</p>}
          {children && (
            <div className={cx(Boolean(title) && 'mt-1', 'text-ink-muted')}>{children}</div>
          )}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 rounded p-1 text-ink-subtle hover:bg-black/5"
            aria-label={t('common.dismiss')}
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
      </div>
    </div>
  )
}

export const EmptyState = ({
  title,
  description,
  action,
  icon,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  icon?: ReactNode
}) => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-[var(--radius-card)] border border-dashed border-border-subtle bg-surface px-6 py-14 text-center">
    {icon && <div className="text-3xl text-ink-subtle">{icon}</div>}
    <p className="text-base font-semibold text-ink">{title}</p>
    {description && <p className="max-w-sm text-sm text-ink-muted">{description}</p>}
    {action}
  </div>
)

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------
export interface FieldWrapperProps {
  label: ReactNode
  hint?: ReactNode
  error?: string
  required?: boolean
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode
}

export const Field = ({ label, hint, error, required, children }: FieldWrapperProps) => {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required && <span className="ms-1 text-danger">*</span>}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint && !error && (
        <p id={hintId} className="text-xs text-ink-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

const controlClasses = (invalid: boolean) =>
  cx(
    'w-full rounded-lg border bg-surface px-3 py-2.5 text-sm text-ink transition',
    'placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand/30',
    invalid ? 'border-danger focus:border-danger' : 'border-border-subtle focus:border-brand',
  )

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  /** Force LTR for values that are always Latin (email, phone, URL). */
  latin?: boolean
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { invalid = false, latin = false, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx(controlClasses(invalid), latin && 'force-ltr', className)}
      {...rest}
    />
  )
})

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { invalid = false, className, rows = 3, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cx(controlClasses(invalid), 'resize-y', className)}
      {...rest}
    />
  )
})

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid = false, className, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx(controlClasses(invalid), 'pe-8', className)}
      {...rest}
    >
      {children}
    </select>
  )
})

export const Checkbox = ({
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) => (
  <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ink">
    <input
      type="checkbox"
      className="size-4 shrink-0 rounded border-border-subtle text-brand accent-[var(--color-brand)]"
      {...rest}
    />
    <span>{label}</span>
  </label>
)

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
export const Modal = ({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}) => {
  const { t } = useTranslation()
  const panelRef = useRef<HTMLDivElement>(null)
  // Callers pass a fresh `onClose` on every render. Read it through a ref so the
  // effect below runs only when the dialog opens -- re-running it focused the
  // panel again after every keystroke, pulling the cursor out of the field.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  // Escape to close, and lock background scrolling while open.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  if (!open) return null

  const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl' }[size]

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/45 p-0 sm:items-center sm:p-4">
      {/* Backdrop click closes; the panel stops propagation. */}
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label={t('common.close')}
        onClick={onClose}
        tabIndex={-1}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className={cx(
          'relative z-10 flex max-h-[92vh] w-full flex-col rounded-t-2xl bg-surface shadow-[var(--shadow-raised)] sm:rounded-2xl',
          widths,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
          <h2 className="text-base font-semibold text-ink sm:text-lg">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="-me-1 rounded-lg p-1.5 text-ink-subtle transition hover:bg-surface-sunken hover:text-ink"
            aria-label={t('common.close')}
          >
            <span aria-hidden="true" className="text-lg leading-none">
              ×
            </span>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-border-subtle px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------
export const Pagination = ({
  page,
  pages,
  onChange,
}: {
  page: number
  pages: number
  onChange: (page: number) => void
}) => {
  const { t } = useTranslation()
  if (pages <= 1) return null

  return (
    <nav
      className="mt-8 flex items-center justify-center gap-3"
      aria-label={t('a11y.pagination')}
    >
      <Button
        variant="secondary"
        size="sm"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
      >
        {/* The chevron mirrors with the document direction. */}
        <span aria-hidden="true" className="flip-rtl">
          ‹
        </span>
        {t('catalog.previousPage')}
      </Button>
      <span className="text-sm text-ink-muted">{t('catalog.page', { page, pages })}</span>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => onChange(page + 1)}
        disabled={page >= pages}
      >
        {t('catalog.nextPage')}
        <span aria-hidden="true" className="flip-rtl">
          ›
        </span>
      </Button>
    </nav>
  )
}

// ---------------------------------------------------------------------------
// Error surface
// ---------------------------------------------------------------------------
export const ErrorBlock = ({
  message,
  reference,
  onRetry,
}: {
  message: string
  reference?: string
  onRetry?: () => void
}) => {
  const { t } = useTranslation()
  return (
    <Alert tone="danger" title={t('errors.title')} className="my-4">
      <p>{message}</p>
      {reference && (
        <p className="mt-1 font-mono text-xs text-ink-subtle force-ltr">
          {t('errors.reference', { id: reference })}
        </p>
      )}
      {onRetry && (
        <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </Alert>
  )
}
